/**
 * PhoneFrame — moldura de celular (390x844) reproduzindo o look do mockup v9.
 *
 * Desktop: mostra uma moldura arredondada centralizada com sombra e status bar fake.
 * Mobile: ocupa a tela inteira (fullscreen).
 */

import { ReactNode } from 'react'

interface PhoneFrameProps {
  children: ReactNode
  /** Oculta a status bar (útil para a tela de câmera em tela cheia) */
  hideStatusBar?: boolean
  /** Cor de fundo da tela (default --bg) */
  bg?: string
}

// Detecta se está rodando num celular real (não no preview desktop)
function isMobileDevice() {
  return window.matchMedia('(max-width: 480px)').matches
}

export default function PhoneFrame({
  children,
  hideStatusBar = false,
  bg,
}: PhoneFrameProps) {
  const isMobile = isMobileDevice()
  // No celular real a status bar nativa já existe — esconde a fake
  const showStatusBar = !hideStatusBar && !isMobile
  return (
    <div
      className="screen"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: isMobile ? '100%' : 390,
        height: '100%',
        maxHeight: isMobile ? '100%' : 844,
        background: bg ?? 'var(--bg)',
        borderRadius: isMobile ? 0 : 36,
        boxShadow: isMobile ? 'none' : 'var(--shadow-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showStatusBar && <StatusBar />}
      <div
        className="phone-content"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {children}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status bar fake (hora + sinal + wifi + bateria) — só visual, sem lógica real
// ---------------------------------------------------------------------------
function StatusBar() {
  const time = new Date().toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return (
    <div
      style={{
        height: 28,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 22px',
        fontSize: 'var(--t-body)',
        fontWeight: 600,
        color: 'var(--text)',
        background: 'transparent',
      }}
    >
      <span>{time}</span>
      <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <svg width="16" height="10" viewBox="0 0 16 10" fill="currentColor">
          <rect x="0" y="7" width="2" height="3" rx="0.5" />
          <rect x="4" y="5" width="2" height="5" rx="0.5" />
          <rect x="8" y="2" width="2" height="8" rx="0.5" />
          <rect x="12" y="0" width="2" height="10" rx="0.5" />
        </svg>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="currentColor">
          <path d="M7 1a9 9 0 0 0-6.36 2.64l1.06 1.06A7.5 7.5 0 0 1 7 2.5a7.5 7.5 0 0 1 5.3 2.2l1.06-1.06A9 9 0 0 0 7 1zm0 3a6 6 0 0 0-4.24 1.76l1.06 1.06A4.5 4.5 0 0 1 7 5.5a4.5 4.5 0 0 1 3.18 1.32l1.06-1.06A6 6 0 0 0 7 4zm0 3a3 3 0 0 0-2.12.88l2.12 2.12 2.12-2.12A3 3 0 0 0 7 7z" />
        </svg>
        <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
          <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke="currentColor" />
          <rect x="2" y="2" width="14" height="6" rx="1" fill="currentColor" />
          <rect x="19.5" y="3" width="1.5" height="4" rx="0.5" fill="currentColor" />
        </svg>
      </span>
    </div>
  )
}
